/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";
import { useBookingData } from "./useBookingData";
import useBookingStatus from "./useBookingStatus";
import { useProcessedBookingEntities } from "./useProcessedBookingEntities";

export const useBookingStatusPage = ({ invoiceId, bookingResponse, initialStatus = null, source = null }) => {
  const {
    invoice,
    bookings,
    firstBooking,
    lastBooking,
    isLoading: isDataLoading,
    isError: isDataError,
    error: dataError,
  } = useBookingData({ invoiceId, bookingResponse });

  const bookingEntities = useProcessedBookingEntities(bookings, invoice);

  const bookingStatusData = useBookingStatus({
    bookingData: firstBooking,
    invoiceData: invoice,
    initialStatus,
    source,
  });

  const { isLoading, isError, error, canRender } = useMemo(() => {
    if (initialStatus) {
      return {
        isLoading: isDataLoading,
        isError: isDataError,
        error: dataError,
        canRender: !isDataLoading && !isDataError,
      };
    }

    const loading = isDataLoading || !bookingStatusData.status;

    return {
      isLoading: loading,
      isError: isDataError,
      error: dataError,
      canRender: !loading && !isDataError,
    };
  }, [isDataLoading, isDataError, dataError, initialStatus, bookingStatusData.status]);

  if (!canRender) {
    return {
      isLoading,
      isError,
      error,
      invoice: null,
      bookings: [],
      firstBooking: null,
      lastBooking: null,
      bookingEntities: {
        services: [],
        service: null,
        provider: null,
        location: null,
        category: null,
      },
      bookingStatus: null,
      statusMetadata: null,
      isBookingConfirmed: false,
      isBookingPending: false,
      isBookingCancelled: false,
      isBookingDelay: false,
      canBeCancelled: false,
      canBeRescheduled: false,
      shouldShowBookingDetails: false,
      shouldShowAddToCalendar: false,
      shouldShowDateLabels: false,
      shouldShowOnlineMeetingLink: false,
    };
  }

  return {
    isLoading: false,
    isError: false,
    error: null,
    invoice,
    bookings,
    firstBooking,
    lastBooking,
    bookingEntities,
    bookingStatus: bookingStatusData.status,
    statusMetadata: bookingStatusData.metadata,
    isBookingConfirmed: bookingStatusData.isConfirmed,
    isBookingPending: bookingStatusData.isPending,
    isBookingCancelled: bookingStatusData.isCancelled,
    isBookingDelay: bookingStatusData.isDelay,
    canBeCancelled: bookingStatusData.canBeCancelled,
    canBeRescheduled: !!firstBooking?.can_reschedule_booking,
    shouldShowBookingDetails: bookingStatusData.shouldShowBookingDetails,
    shouldShowAddToCalendar: bookingStatusData.shouldShowAddToCalendar,
    shouldShowDateLabels: bookingStatusData.shouldShowDateLabels,
    shouldShowOnlineMeetingLink: bookingStatusData.shouldShowOnlineMeetingLink,
  };
};
